import { db } from '../../services/firebase/config';
import { doc, getDoc, setDoc, updateDoc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { handleFirestoreError, withFirestoreErrorHandling } from '../../utils/firestoreInterceptor';

const USERS_COLLECTION = 'users';

// Note: handleFirestoreAuthError remplacé par l'intercepteur plus robuste

/**
 * Crée ou met à jour un utilisateur dans Firestore
 * @param {string} uid - L'identifiant de l'utilisateur
 * @param {object} userData - Les données de l'utilisateur à enregistrer
 * @returns {Promise<void>}
 */
export const createOrUpdateUser = async (uid, userData) => {
  return withFirestoreErrorHandling(
    async () => {
      const userRef = doc(db, USERS_COLLECTION, uid);
      
      // Vérifier si l'utilisateur existe déjà
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        // Mise à jour de l'utilisateur existant, sans écraser les champs existants
        await updateDoc(userRef, {
          ...userData,
          lastLogin: new Date(),
          updatedAt: new Date()
        });
        return { ...userDoc.data(), ...userData, id: uid };
      } else {
        // Création d'un nouvel utilisateur
        const newUser = {
          ...userData,
          createdAt: new Date(),
          lastLogin: new Date(),
          updatedAt: new Date(),
          isActive: true,
          isAdmin: userData.isAdmin || false
        };
        await setDoc(userRef, newUser);
        return { ...newUser, id: uid };
      }
    },
    'création/mise à jour du profil utilisateur'
  );
};

/**
 * Récupère un utilisateur depuis Firestore
 * @param {string} uid - L'identifiant de l'utilisateur
 * @returns {Promise<object|null>} - Les données de l'utilisateur ou null si l'utilisateur n'existe pas
 */
export const getUserProfile = async (uid) => {
  return withFirestoreErrorHandling(
    async () => {
      const userRef = doc(db, USERS_COLLECTION, uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Vérifier si displayName est présent
        if (!userData.displayName) {

        }
        
        return { ...userData, id: uid };
      }

      return null;
    },
    'récupération du profil utilisateur'
  );
};

/**
 * Récupère tous les utilisateurs depuis Firestore
 * @returns {Promise<Array>} - Liste des utilisateurs
 */
export const getAllUsers = async () => {
  try {
    const usersRef = collection(db, USERS_COLLECTION);
    const snapshot = await getDocs(usersRef);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {

    throw error;
  }
};

/**
 * Met à jour le statut administrateur d'un utilisateur
 * @param {string} uid - L'identifiant de l'utilisateur
 * @param {boolean} isAdmin - Le nouveau statut administrateur
 * @returns {Promise<object>} - Les données de l'utilisateur mises à jour
 */
export const updateUserAdminStatus = async (uid, isAdmin) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error(`L'utilisateur avec l'ID ${uid} n'existe pas`);
    }
    
    await updateDoc(userRef, {
      isAdmin: isAdmin,
      updatedAt: new Date()
    });
    
    return {
      ...userDoc.data(),
      isAdmin,
      id: uid
    };
  } catch (error) {

    throw error;
  }
};

/**
 * Vérifie si un utilisateur est administrateur
 * @param {string} uid - L'identifiant de l'utilisateur
 * @returns {Promise<boolean>} - Vrai si l'utilisateur est administrateur, faux sinon
 */
export const isUserAdmin = async (uid) => {
  try {
    const userProfile = await getUserProfile(uid);
    return userProfile?.isAdmin === true;
  } catch (error) {

    return false;
  }
};

/**
 * Met à jour le profil d'un utilisateur dans Firestore
 * @param {string} uid - L'identifiant de l'utilisateur
 * @param {object} profileData - Les données du profil à mettre à jour
 * @returns {Promise<object>} - Les données de l'utilisateur mises à jour
 */
export const updateUserProfile = async (uid, profileData) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, uid);
    
    // Vérifier si l'utilisateur existe
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error(`L'utilisateur avec l'ID ${uid} n'existe pas`);
    }
    
    // Log des données actuelles du profil pour débogage
    const currentData = userDoc.data();
    
    // Traitement spécial pour le displayName
    if (profileData.displayName === undefined || profileData.displayName === null) {

      if (currentData.displayName) {
        profileData.displayName = currentData.displayName;
      }
    } else {
      
      // Pour Azure, s'assurer que le displayName est enregistré directement
      if (currentData.isAzureUser || currentData.authProvider === 'azure' || uid.startsWith('azure-')) {
        
        // S'assurer que les données Azure importantes sont préservées
        if (currentData.azureId && !profileData.azureId) {
          profileData.azureId = currentData.azureId;
        }
        
        if (currentData.isAzureUser && profileData.isAzureUser === undefined) {
          profileData.isAzureUser = true;
        }
        
        if (currentData.authProvider && !profileData.authProvider) {
          profileData.authProvider = currentData.authProvider;
        }
      }
    }
    
    // S'assurer que les settings sont préservés s'ils ne sont pas mis à jour
    if (!profileData.settings && currentData.settings) {
      profileData.settings = currentData.settings;
    }
    
    // S'assurer que le rôle est préservé s'il n'est pas mis à jour
    if (!profileData.role && currentData.role) {
      profileData.role = currentData.role;
    }
    
    // Mise à jour des données du profil avec la date de mise à jour
    const updateData = {
      ...profileData,
      updatedAt: new Date()
    };

    await updateDoc(userRef, updateData);
    
    // Récupérer et retourner les données mises à jour
    const updatedUserDoc = await getDoc(userRef);
    const updatedData = updatedUserDoc.data();
    
    return { ...updatedData, id: uid };
  } catch (error) {

    throw error;
  }
};

/**
 * Supprime un utilisateur de Firestore
 * @param {string} uid - L'identifiant de l'utilisateur à supprimer
 * @returns {Promise<void>}
 */
export const deleteUser = async (uid) => {
  return withFirestoreErrorHandling(
    async () => {
      const userRef = doc(db, USERS_COLLECTION, uid);
      
      // Vérifier si l'utilisateur existe
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error(`L'utilisateur avec l'ID ${uid} n'existe pas`);
      }
      
      // Supprimer le document utilisateur
      await deleteDoc(userRef);

    },
    'suppression de l\'utilisateur'
  );
}; 